import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { User, LogOut, MapPin, BookOpen, ExternalLink, Pencil, Check, X, GraduationCap, Briefcase, Building2, School, KeyRound, Loader2, Plus, Copy, Trash2, Link2, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useBasePath } from '@/contexts/PortalContext';
import TerrainBackground from '@/components/assessment/TerrainBackground';

const profileSchema = z.object({
  display_name: z.string().min(1, 'Name is required').max(80),
  location: z.string().max(80).optional(),
  grade: z.string().max(40).optional(),
});
type ProfileFormData = z.infer<typeof profileSchema>;

const EDITION_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  highschool: { label: 'High School', icon: GraduationCap, color: 'text-primary bg-primary/10' },
  college: { label: 'College', icon: BookOpen, color: 'text-accent bg-accent/10' },
  professional: { label: 'Professional', icon: Briefcase, color: 'text-foreground bg-secondary' },
};

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const staggerCard = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: 'spring' as const, stiffness: 200, damping: 22 } },
};

const Dashboard = () => {
  const { user, profile, signOut, updateProfile, fetchProfile } = useAuth();
  const basePath = useBasePath();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schoolCode, setSchoolCode] = useState('');
  const [joiningSchool, setJoiningSchool] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joinSuccess, setJoinSuccess] = useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      display_name: profile?.display_name ?? '',
      location: profile?.location ?? '',
      grade: profile?.grade ?? '',
    },
  });

  const [generatingCode, setGeneratingCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');

  const { data: accessCodes, refetch: refetchCodes } = useQuery({
    queryKey: ['access_codes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('access_codes' as never)
        .select('id, code, label, max_uses, uses_count, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as { id: string; code: string; label: string | null; max_uses: number; uses_count: number; created_at: string }[];
    },
    enabled: !!profile?.is_admin,
  });

  const generateCode = async () => {
    setGeneratingCode(true);
    await supabase.rpc('generate_access_code' as never, { p_label: 'Invite link', p_max_uses: 50 });
    await refetchCodes();
    setGeneratingCode(false);
  };

  const revokeCode = async (code: string) => {
    await supabase.rpc('revoke_access_code' as never, { p_code: code });
    await refetchCodes();
  };

  const copyInviteLink = (code: string) => {
    const url = `${window.location.origin}/auth?code=${code}`;
    navigator.clipboard.writeText(url);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const { data: assessments, isLoading: assessmentsLoading } = useQuery({
    queryKey: ['assessments', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assessment_results')
        .select('id, share_id, edition, created_at, results')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const onJoinSchool = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schoolCode.trim()) return;
    setJoiningSchool(true);
    setJoinError('');
    setJoinSuccess('');
    const { error } = await supabase.rpc('join_school_by_code' as never, { p_school_code: schoolCode.trim().toUpperCase() });
    if (error) {
      setJoinError(error.message.includes('Invalid school code') ? 'Invalid school code. Check with your counselor.' : error.message);
    } else {
      setJoinSuccess('School linked! Your path will now include your school\'s courses.');
      setSchoolCode('');
      if (user?.id) fetchProfile(user.id);
    }
    setJoiningSchool(false);
  };

  const onSave = async (data: ProfileFormData) => {
    setSaving(true);
    await updateProfile({
      display_name: data.display_name,
      location: data.location || null,
      grade: data.grade || null,
    });
    setSaving(false);
    setEditing(false);
  };

  const displayName = profile?.display_name || user?.email?.split('@')[0] || 'Your Account';

  return (
    <div className="min-h-screen bg-journey-atmosphere relative overflow-hidden">
      <TerrainBackground count={4} />

      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-border/30 bg-background/80 backdrop-blur-md sticky top-0 z-20">
        <Link to={basePath || '/'} className="text-primary font-display text-lg font-bold">TruePath</Link>
        <div className="flex items-center gap-3">
          <Link to={`${basePath}/admin`} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" aria-label="School portal">
            <School className="w-3.5 h-3.5" aria-hidden="true" /> School Portal
          </Link>
          <button
            onClick={signOut}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Sign out"
          >
            <LogOut className="w-3.5 h-3.5" aria-hidden="true" /> Sign out
          </button>
        </div>
      </header>

      <main id="main-content" className="max-w-2xl mx-auto px-4 py-10 relative z-10 space-y-8">

        {/* Section 1: Profile Card */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          aria-labelledby="profile-heading"
          className="glass-card p-6 space-y-4"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h1 id="profile-heading" className="text-xl font-display font-bold text-foreground">{displayName}</h1>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            {!editing && (
              <button
                onClick={() => {
                  reset({
                    display_name: profile?.display_name ?? '',
                    location: profile?.location ?? '',
                    grade: profile?.grade ?? '',
                  });
                  setEditing(true);
                }}
                aria-label="Edit profile"
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Pencil className="w-3 h-3" aria-hidden="true" /> Edit
              </button>
            )}
          </div>

          {!editing ? (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              {profile?.location && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  <dt className="sr-only">Location</dt>
                  <dd>{profile.location}</dd>
                </div>
              )}
              {profile?.grade && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Building2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  <dt className="sr-only">Grade / Year</dt>
                  <dd>{profile.grade}</dd>
                </div>
              )}
            </dl>
          ) : (
            <form onSubmit={handleSubmit(onSave)} aria-label="Edit profile form" className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="display_name" className="text-xs font-medium text-foreground">Name</label>
                <input
                  id="display_name"
                  {...register('display_name')}
                  className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground text-sm focus:ring-2 focus:ring-primary"
                  aria-invalid={!!errors.display_name}
                />
                {errors.display_name && (
                  <p role="alert" className="text-xs text-destructive">{errors.display_name.message}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label htmlFor="location" className="text-xs font-medium text-foreground">Location</label>
                  <input
                    id="location"
                    {...register('location')}
                    placeholder="City, Country"
                    className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground text-sm focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="grade" className="text-xs font-medium text-foreground">Grade / Year</label>
                  <input
                    id="grade"
                    {...register('grade')}
                    placeholder="e.g. Grade 11"
                    className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground text-sm focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="submit"
                  disabled={saving}
                  aria-busy={saving}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50"
                >
                  <Check className="w-3 h-3" aria-hidden="true" /> {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3" aria-hidden="true" /> Cancel
                </button>
              </div>
            </form>
          )}
        </motion.section>

        {/* Section 1b: Join School (shown only when no school linked) */}
        {!profile?.school_id && (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            aria-labelledby="school-heading"
            className="glass-card p-5 space-y-3"
          >
            <div className="flex items-center gap-2">
              <School className="w-4 h-4 text-primary" aria-hidden="true" />
              <h2 id="school-heading" className="text-sm font-semibold text-foreground">Link Your School</h2>
            </div>
            <p className="text-xs text-muted-foreground">Enter the code your school or counselor gave you. Your results will include your school's actual courses and clubs.</p>
            <form onSubmit={onJoinSchool} aria-label="Join school form" className="flex gap-2">
              <input
                id="school-code"
                type="text"
                value={schoolCode}
                onChange={e => setSchoolCode(e.target.value.toUpperCase())}
                placeholder="ABCD1234"
                maxLength={8}
                aria-label="School code"
                className="flex-1 px-3 py-2 rounded-lg bg-secondary border border-border text-foreground font-mono text-sm tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button type="submit" disabled={joiningSchool || !schoolCode.trim()}
                aria-busy={joiningSchool}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50">
                {joiningSchool ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <KeyRound className="w-3.5 h-3.5" aria-hidden="true" />}
                {joiningSchool ? 'Joining...' : 'Join'}
              </button>
            </form>
            {joinError && <p role="alert" aria-live="assertive" className="text-xs text-destructive">{joinError}</p>}
            {joinSuccess && <p role="status" aria-live="polite" className="text-xs text-primary">{joinSuccess}</p>}
          </motion.section>
        )}

        {/* Section 1c: Access Codes (admin only) */}
        {profile?.is_admin && (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            aria-labelledby="codes-heading"
            className="glass-card p-5 space-y-3"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-primary" aria-hidden="true" />
                <h2 id="codes-heading" className="text-sm font-semibold text-foreground">Invite Codes</h2>
              </div>
              <button
                onClick={generateCode}
                disabled={generatingCode}
                aria-busy={generatingCode}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50"
              >
                {generatingCode ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> : <Plus className="w-3 h-3" aria-hidden="true" />}
                {generatingCode ? 'Generating…' : 'New Code'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Share these links to give people access. Each code supports 50 signups.</p>
            {(!accessCodes || accessCodes.length === 0) && (
              <p className="text-xs text-muted-foreground/60 text-center py-2">No codes yet. Click "New Code" to generate one.</p>
            )}
            {accessCodes && accessCodes.length > 0 && (
              <ul className="space-y-2" aria-label="Access codes">
                {accessCodes.map((ac) => (
                  <li key={ac.id} className="flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-2">
                    <code className="font-mono text-xs text-primary tracking-widest flex-1">{ac.code}</code>
                    <span className="text-[10px] text-muted-foreground shrink-0">{ac.uses_count}/{ac.max_uses} used</span>
                    <button
                      onClick={() => copyInviteLink(ac.code)}
                      aria-label={copiedCode === ac.code ? 'Copied!' : `Copy invite link for ${ac.code}`}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    >
                      {copiedCode === ac.code ? <Check className="w-3 h-3 text-primary" aria-hidden="true" /> : <Copy className="w-3 h-3" aria-hidden="true" />}
                      {copiedCode === ac.code ? 'Copied' : 'Copy link'}
                    </button>
                    <button
                      onClick={() => revokeCode(ac.code)}
                      aria-label={`Revoke code ${ac.code}`}
                      className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    >
                      <Trash2 className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </motion.section>
        )}

        {/* Section 2: Assessment History */}
        <section aria-labelledby="history-heading">
          <div className="flex items-center justify-between mb-4">
            <h2 id="history-heading" className="text-xl font-display font-bold text-foreground">Your Paths</h2>
            {assessments && assessments.length > 0 && (
              <Link
                to={`${basePath}/assessment`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Retake Assessment
              </Link>
            )}
          </div>

          {assessmentsLoading && (
            <div className="flex items-center justify-center py-12" aria-busy="true" aria-label="Loading your paths">
              <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-hidden="true" />
            </div>
          )}

          {!assessmentsLoading && (!assessments || assessments.length === 0) && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-8 text-center space-y-4"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                <MapPin className="w-7 h-7 text-primary" aria-hidden="true" />
              </div>
              <h3 className="font-display font-semibold text-foreground">No paths yet</h3>
              <p className="text-sm text-muted-foreground">Take an assessment to discover your personalized career path.</p>
              <Link
                to={`${basePath}/assessment`}
                className="inline-block px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110 transition-all"
              >
                Start your path
              </Link>
            </motion.div>
          )}

          {!assessmentsLoading && assessments && assessments.length > 0 && (
            <motion.ul
              variants={staggerContainer}
              initial="hidden"
              animate="show"
              className="space-y-3"
              aria-label="Assessment history"
            >
              {assessments.map((a) => {
                const meta = EDITION_META[a.edition] || EDITION_META.professional;
                const EditionIcon = meta.icon;
                const results = a.results as Record<string, unknown> | null;
                const snippet = (results as { youSummary?: { brainWiring?: string } })?.youSummary?.brainWiring;
                const date = new Date(a.created_at).toLocaleDateString('en-CA', {
                  year: 'numeric', month: 'short', day: 'numeric',
                });

                return (
                  <motion.li key={a.id} variants={staggerCard}>
                    <div className="glass-card p-4 flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}>
                        <EditionIcon className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-foreground">{meta.label} Path</span>
                          <time className="text-xs text-muted-foreground shrink-0" dateTime={a.created_at}>{date}</time>
                        </div>
                        {snippet && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{snippet}</p>
                        )}
                      </div>
                      <Link
                        to={`/shared/${a.share_id}`}
                        aria-label={`View ${meta.label} path from ${date}`}
                        className="shrink-0 flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        View <ExternalLink className="w-3 h-3" aria-hidden="true" />
                      </Link>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ul>
          )}
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
